/**
 * CollateralMapPDFTemplate — React component rendered off-screen for dom-to-image-more capture
 * O1 v42: Refactored from HTML string (buildCollateralMapPDFHtml) to React component
 *         to match LoanPDFTemplate.tsx pattern — fonts pre-loaded in DOM, no Google Fonts hang
 *
 * DESIGN RULES (same as LoanPDFTemplate):
 * - ບໍ່ໃຊ້ border ຮອບ section (ໃຊ້ background color ແທນ)
 * - ໃຊ້ inline styles ທີ່ເປັນ hex colors ທັງໝົດ (ບໍ່ oklch)
 * - fontFamily: 'Noto Sans Lao', 'Phetsarath OT', Arial — ໃຊ້ font ທີ່ app ໂຫຼດແລ້ວ
 * - width: 794px (A4 at 96dpi)
 * - expose wrapper ref for full-template capture
 * X3/X4 v51: Redesigned to match LoanPDFTemplate — removed all borders, clean sections
 */
import { forwardRef, useImperativeHandle, useRef } from "react";
import {
  formatAreaLao,
  formatAreaBreakdown,
} from "../lib/areaUnitConversion";

export interface CollateralMapPDFTemplateProps {
  collateralId: number;
  collateralType?: string;
  ownerName?: string;
  areaSqM: number;
  perimeterM: number;
  gpsLatitude?: string | number;
  gpsLongitude?: string | number;
  updatedAt?: string | null;
  mapImageBase64?: string;
  segmentLengthsM?: number[];
  mapPolicyDisclosure?: string;
}

export interface CollateralMapPDFTemplateHandle {
  wrapper: HTMLDivElement | null;
}

const COLLATERAL_TYPE_MAP: Record<string, string> = {
  land: "ທີ່ດິນ",
  building: "ເຮືອນ/ອາຄານ",
  vehicle: "ລົດ",
  deposit: "ເງິນຝາກ",
  other: "ອື່ນໆ",
};

function fmtPerim(meters: number): string {
  if (!isFinite(meters) || meters <= 0) return "-";
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(3)} km (${Math.round(meters).toLocaleString("en-US")} m)`;
  }
  return `${Math.round(meters).toLocaleString("en-US")} m`;
}

function fmtGps(lat?: string | number, lng?: string | number): string {
  if (!lat || !lng) return "-";
  return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
}

function fmtDate(dateStr?: string | null): string {
  if (!dateStr) return new Date().toLocaleDateString("lo-LA", { year: "numeric", month: "long", day: "numeric" });
  return new Date(dateStr).toLocaleDateString("lo-LA", { year: "numeric", month: "long", day: "numeric" });
}

const CollateralMapPDFTemplate = forwardRef<
  CollateralMapPDFTemplateHandle,
  CollateralMapPDFTemplateProps
>((props, ref) => {
  const {
    collateralId,
    collateralType,
    ownerName,
    areaSqM,
    perimeterM,
    gpsLatitude,
    gpsLongitude,
    updatedAt,
    mapImageBase64,
    segmentLengthsM,
    mapPolicyDisclosure,
  } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => ({
    get wrapper() { return wrapperRef.current; },
  }));

  const typeLabel = collateralType
    ? (COLLATERAL_TYPE_MAP[collateralType] || collateralType)
    : "ຫຼັກຊັບ";
  const areaLao = areaSqM > 0 ? formatAreaLao(areaSqM) : "ຍັງບໍ່ໄດ້ວັດ";
  const areaM2 = areaSqM > 0 ? `${Math.round(areaSqM).toLocaleString("en-US")} ຕາແມັດ` : "-";
  const areaRai = areaSqM > 0 ? (areaSqM / 1600).toFixed(2) : "-";
  const areaHa = areaSqM > 0 ? (areaSqM / 10000).toFixed(4) : "-";
  const areaBreakdown = formatAreaBreakdown(areaSqM);
  const perimLabel = fmtPerim(perimeterM);
  const gpsLabel = fmtGps(gpsLatitude, gpsLongitude);
  const dateLabel = fmtDate(updatedAt);

  // V1 v48 / X3 v51: Prevent shadow/ghost artifacts when dom-to-image captures at opacity:0.001
  // P1 v50: TD_VALUE_LAO — Lao text cells use Noto Sans Lao (monospace cannot render Lao glyphs)
  const TD_VALUE_LAO: React.CSSProperties = {
    fontFamily: "'Noto Sans Lao', 'Phetsarath OT', Arial, sans-serif",
    color: "#374151",
    padding: "6px 12px",
  };
  // TD_VALUE_MONO — numeric cells use monospace for alignment
  const TD_VALUE_MONO: React.CSSProperties = {
    fontFamily: "monospace",
    color: "#374151",
    padding: "6px 12px",
  };

  const BASE: React.CSSProperties = {
    fontFamily: "'Noto Sans Lao', 'Phetsarath OT', Arial, sans-serif",
    backgroundColor: "#ffffff",
    color: "#1e293b",
    fontSize: "12px",
    width: "794px",
    boxShadow: "none",
    filter: "none",
    outline: "none",
  };

  return (
    <div ref={wrapperRef} data-pdf-template="collateral-map" style={BASE}>

      {/* ===== HEADER SECTION (matches LoanPDFTemplate) ===== */}
      <div style={{ padding: "28px 36px 20px 36px", backgroundColor: "#ffffff" }}>
        {/* Logo + Title row */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "3px solid #002975",
          paddingBottom: "14px",
          marginBottom: "18px",
        }}>
          {/* P2 v50: SVG inline logo — no /logo.png dependency, no CORS */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
            <div style={{
              width: "44px",
              height: "44px",
              background: "#002975",
              borderRadius: "6px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
              <span style={{ color: "#ffffff", fontWeight: 800, fontSize: "14px", letterSpacing: "0.5px", lineHeight: 1 }}>CRS</span>
              <span style={{ color: "#93c5fd", fontSize: "7px", letterSpacing: "0.3px", lineHeight: 1, marginTop: "2px" }}>CREDIT</span>
            </div>
            <div>
              <div style={{ fontSize: "24px", fontWeight: 900, color: "#002975", letterSpacing: "-0.5px", lineHeight: 1 }}>
                ລາຍງານຂອບເຂດຫຼັກຊັບ
              </div>
              <div style={{ fontSize: "10px", color: "#64748b", marginTop: "2px" }}>
                {typeLabel} — ລະຫັດ #{collateralId}
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "11px", color: "#64748b", lineHeight: "1.7" }}>
              <div><strong style={{ color: "#374151" }}>ວັນທີ:</strong> {dateLabel}</div>
              {ownerName && <div><strong style={{ color: "#374151" }}>ເຈົ້າຂອງ:</strong> {ownerName}</div>}
              <div style={{ color: "#94a3b8", fontSize: "10px", marginTop: "2px" }}>Credit Referral System</div>
            </div>
          </div>
        </div>

        {/* Summary Cards (matches LoanPDFTemplate style) */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
          gap: "8px",
          marginBottom: "18px",
        }}>
          <div style={{ backgroundColor: "#f0f4ff", padding: "10px 12px", borderRadius: "6px" }}>
            <div style={{ fontSize: "9px", color: "#6b7280", marginBottom: "3px" }}>ຕາແມັດ (m²)</div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "#002975", fontFamily: "monospace" }}>{areaM2}</div>
          </div>
          <div style={{ backgroundColor: "#eff6ff", padding: "10px 12px", borderRadius: "6px" }}>
            <div style={{ fontSize: "9px", color: "#6b7280", marginBottom: "3px" }}>ໄຮ (rai)</div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "#1e40af", fontFamily: "monospace" }}>{areaRai}</div>
          </div>
          <div style={{ backgroundColor: "#f0fdf4", padding: "10px 12px", borderRadius: "6px" }}>
            <div style={{ fontSize: "9px", color: "#6b7280", marginBottom: "3px" }}>ha (ເຮັກຕ້າ)</div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "#15803d", fontFamily: "monospace" }}>{areaHa}</div>
          </div>
          <div style={{ backgroundColor: "#fff7ed", padding: "10px 12px", borderRadius: "6px" }}>
            <div style={{ fontSize: "9px", color: "#6b7280", marginBottom: "3px" }}>ເສັ້ນຮອບວົງ</div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "#c2410c", fontFamily: "monospace" }}>{perimLabel}</div>
          </div>
        </div>
      </div>

      {/* ===== MAP IMAGE ===== */}
      <div style={{ padding: "0 36px 18px 36px" }}>
        {mapImageBase64 ? (
          // Z2/Z3 v54: height:250px (was maxHeight:380px) so full template fits on 1 A4 page
          <img
            src={mapImageBase64}
            alt="ແຜນທີ່ຂອບເຂດ"
            style={{
              width: "100%",
              height: "250px",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
              borderRadius: "4px",
            }}
          />
        ) : (
          <div style={{
            height: "200px",
            backgroundColor: "#f8fafc",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#94a3b8",
            fontSize: "13px",
          }}>
            ບໍ່ມີຮູບແຜນທີ່
          </div>
        )}
      </div>

      {/* ===== MEASUREMENTS TABLE ===== */}
      <div style={{ padding: "0 36px 18px 36px" }}>
        <div style={{
          fontSize: "11px",
          fontWeight: 700,
          color: "#002975",
          marginBottom: "8px",
          letterSpacing: "0.3px",
          textTransform: "uppercase",
        }}>
          ຂໍ້ມູນການວັດແທກ
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
          <tbody>
            {/* Area summary row — highlighted */}
            <tr style={{ backgroundColor: "#eff6ff" }}>
              <td style={{ padding: "7px 12px", fontWeight: 600, color: "#374151", width: "40%", backgroundColor: "#f1f5f9" }}>
                ເນື້ອທີ່
              </td>
              <td style={{ padding: "7px 12px", color: "#002975", fontWeight: 700, fontSize: "12px", fontFamily: "monospace" }}>
                {areaM2}
              </td>
            </tr>
            <tr style={{ backgroundColor: "#ffffff" }}>
              <td style={{ padding: "6px 12px", fontWeight: 600, color: "#374151", backgroundColor: "#f8fafc" }}>ໄຮ (rai)</td>
              <td style={{ padding: "6px 12px", fontFamily: "monospace", color: "#1e40af", fontWeight: 600 }}>{areaRai} ໄຮ</td>
            </tr>
            <tr style={{ backgroundColor: "#f8fafc" }}>
              <td style={{ padding: "6px 12px", fontWeight: 600, color: "#374151", backgroundColor: "#f8fafc" }}>ha (ເຮັກຕ້າ)</td>
              <td style={{ padding: "6px 12px", fontFamily: "monospace", color: "#15803d", fontWeight: 600 }}>{areaHa} ha</td>
            </tr>
            <tr style={{ backgroundColor: "#ffffff" }}>
              <td style={{ padding: "6px 12px", fontWeight: 600, color: "#374151", backgroundColor: "#f8fafc" }}>ເສັ້ນຮອບວົງ</td>
              <td style={{ padding: "6px 12px", fontFamily: "monospace", color: "#374151" }}>{perimLabel}</td>
            </tr>
            <tr style={{ backgroundColor: "#f8fafc" }}>
              <td style={{ padding: "6px 12px", fontWeight: 600, color: "#374151", backgroundColor: "#f8fafc" }}>ພິກັດ GPS</td>
              <td style={{ padding: "6px 12px", fontFamily: "monospace", color: "#374151" }}>{gpsLabel}</td>
            </tr>
            <tr style={{ backgroundColor: "#ffffff" }}>
              <td style={{ padding: "6px 12px", fontWeight: 600, color: "#374151", backgroundColor: "#f8fafc" }}>ປະເພດຫຼັກຊັບ</td>
              {/* P1 v50: Lao text — Noto Sans Lao */}
              <td style={{ padding: "6px 12px", fontFamily: "'Noto Sans Lao', 'Phetsarath OT', Arial, sans-serif", color: "#374151" }}>{typeLabel}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ===== SEGMENT LENGTHS TABLE ===== */}
      {segmentLengthsM && segmentLengthsM.length > 0 && (
        <div style={{ padding: "0 36px 18px 36px" }}>
          <div style={{
            fontSize: "11px",
            fontWeight: 700,
            color: "#002975",
            marginBottom: "8px",
            letterSpacing: "0.3px",
            textTransform: "uppercase",
          }}>
            ຄວາມຍາວແຕ່ລະດ້ານ
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
            <thead>
              <tr style={{ backgroundColor: "#002975", color: "#ffffff" }}>
                <th style={{ padding: "7px 10px", textAlign: "left", fontWeight: 600, letterSpacing: "0.3px" }}>ດ້ານ</th>
                <th style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600, letterSpacing: "0.3px" }}>ເມັດ (m)</th>
                <th style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600, letterSpacing: "0.3px" }}>ກິໂລ (km)</th>
              </tr>
            </thead>
            <tbody>
              {segmentLengthsM.map((len, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                  <td style={{ padding: "6px 10px", fontWeight: 600, color: "#374151" }}>ດ້ານ {i + 1}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "monospace", color: "#374151" }}>
                    {Math.round(len).toLocaleString("en-US")}
                  </td>
                  <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "monospace", color: "#64748b" }}>
                    {(len / 1000).toFixed(3)}
                  </td>
                </tr>
              ))}
              <tr style={{ backgroundColor: "#eff6ff", fontWeight: 700 }}>
                <td style={{ padding: "7px 10px", color: "#1e40af" }}>ລວມ</td>
                <td style={{ padding: "7px 10px", textAlign: "right", color: "#1e40af", fontFamily: "monospace" }}>
                  {Math.round(perimeterM).toLocaleString("en-US")}
                </td>
                <td style={{ padding: "7px 10px", textAlign: "right", color: "#1e40af", fontFamily: "monospace" }}>
                  {(perimeterM / 1000).toFixed(3)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ===== FOOTER NOTE ===== */}
      <div style={{ padding: "0 36px 28px 36px" }}>
        {mapPolicyDisclosure && (
          <div style={{
            marginBottom: "8px",
            padding: "9px 12px",
            backgroundColor: "#eff6ff",
            borderLeft: "3px solid #002975",
            borderRadius: "4px",
            fontSize: "10px",
            color: "#1e3a5f",
            lineHeight: 1.5,
          }}>
            <strong>ຄວາມໝາຍຂອງແຜນທີ່: </strong>{mapPolicyDisclosure}
          </div>
        )}
        <div style={{
          padding: "10px 14px",
          backgroundColor: "#fef9c3",
          borderRadius: "6px",
          fontSize: "10px",
          color: "#92400e",
        }}>
          ⚠️ ຂໍ້ມູນນີ້ຜະລິດຈາກລະບົບ Credit Referral System — ໃຊ້ເພື່ອອ້າງອີງເທົ່ານັ້ນ ບໍ່ໃຊ້ແທນເອກະສານທາງການ
        </div>
        <p style={{ textAlign: "center", marginTop: "12px", color: "#94a3b8", fontSize: "10px" }}>
          Smart Credit Referral System — ສ້າງວັນທີ {new Date().toLocaleDateString("lo-LA")}
        </p>
      </div>
      {/* T1 v46 FIX: Removed trailing 40px spacer div — caused content to overflow into blank page 2 */}
    </div>
  );
});

CollateralMapPDFTemplate.displayName = "CollateralMapPDFTemplate";
export default CollateralMapPDFTemplate;
